import { GET_DREAMS_TOKENS_KEY } from '@/pages/editor/components/hooks/useDreamsTokens';
import { createDream as createDreamApi } from '@/services/api';
import { logger } from '@/services/logger';
import { isImageProcessing } from '@/state/presenters/presenters';
import { getImageSize } from '@/utils/utils';
import { IDream } from '@d-id/types';
import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { useSetRecoilState } from 'recoil';
import useDreamsMetadata from './useDreamsMetadata';

export enum DreamsStatus {
    SUCCESS = 'SUCCESS',
    FACE_NOT_DETECTED = 'FACE_NOT_DETECTED',
    MODERATION_FAILED = 'MODERATION_FAILED',
    CELEBRITY_DETECTED = 'CELEBRITY_DETECTED',
    DREAMS_INTERNAL_ERROR = 'DREAMS_INTERNAL_ERROR',
}

export type IDreamsResult = {
    id: string;
    url: string;
    promptId: string;
    status: DreamsStatus;
    createdAt: string;
}[];

function buildPrompt(prefix: string, prompt: string, suffixes: string[]) {
    return `${prefix} ${suffixes.join(' ')} ${prompt}`;
}

function getDreamStatus(dream: IDream): DreamsStatus {
    if (dream.kind === 'ImageModerationError') {
        return DreamsStatus.MODERATION_FAILED;
    } else if (dream.kind === 'CelebrityDetectedError') {
        return DreamsStatus.CELEBRITY_DETECTED;
    } else if (dream.kind === 'FaceError') {
        return DreamsStatus.FACE_NOT_DETECTED;
    }

    return DreamsStatus.SUCCESS;
}

export const useCreateDream = () => {
    const setIsImageProcessing = useSetRecoilState(isImageProcessing);
    const [dreamsResult, setDreamsResult] = useState<IDreamsResult>([]);
    const { data: metadata } = useDreamsMetadata();
    const queryClient = useQueryClient();

    const createDream = async (prefix: string, prompt: string): Promise<IDreamsResult> => {
        try {
            setIsImageProcessing(true);
            const { used, limit, images } = await createDreamApi(prompt, buildPrompt(prefix, prompt, metadata?.suffixes ?? []));
            logger.debug('Dreams response', images);
            const uploads = images.map(async dream => {
                const url = dream.details?.url ?? dream.poster;
                await getImageSize(url);

                return {
                    url,
                    id: dream.id,
                    promptId: dream.prompt_id,
                    createdAt: dream.created_at,
                    status: getDreamStatus(dream),
                };
            });

            queryClient.setQueriesData(GET_DREAMS_TOKENS_KEY, { used, limit });

            return await Promise.all(uploads);
        } catch (e) {
            throw e;
        } finally {
            setIsImageProcessing(false);
        }
    };

    return {
        createDream,
        dreamsResult,
        setDreamsResult,
    };
};
